import { Plain, Rich } from '@agendor/shared';

/** A weekly grid as the editor holds it: `'HH:MM'` strings, keyed by weekday. */
export type OpeningHoursDraft = Record<Plain.WeekdayName, DraftInterval[]>;

export type DraftInterval = { start: string; end: string };

export type PlainOpeningHours = Record<Plain.WeekdayName, Plain.DayTimeSlice[]>;

export const WEEKDAYS: readonly Plain.WeekdayName[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const WEEKDAY_LABELS: Record<Plain.WeekdayName, string> = {
    sun: 'Domingo',
    mon: 'Segunda',
    tue: 'Terça',
    wed: 'Quarta',
    thu: 'Quinta',
    fri: 'Sexta',
    sat: 'Sábado',
};

/** The grid keeps midnight as `24:00`; a reader expects to see `00:00`. */
export function displayEnd(time: string): string {
    return time === '24:00' ? '00:00' : time;
}

function toMinutes(time: string): number {
    const [hour, minute] = time.split(':').map(Number);
    return hour * 60 + minute;
}

function fromMinutes(minutes: number): string {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export function emptyDraft(): OpeningHoursDraft {
    const draft = {} as OpeningHoursDraft;
    for (const weekday of WEEKDAYS) {
        draft[weekday] = [];
    }
    return draft;
}

export function toDraft(hours: PlainOpeningHours): OpeningHoursDraft {
    const draft = {} as OpeningHoursDraft;
    for (const weekday of WEEKDAYS) {
        draft[weekday] = hours[weekday].map((interval) => ({
            start: formatPlainTime(interval.start),
            end: formatPlainTime(interval.end),
        }));
    }
    return draft;
}

export function fromDraft(draft: OpeningHoursDraft): PlainOpeningHours {
    const hours = {} as PlainOpeningHours;
    for (const weekday of WEEKDAYS) {
        hours[weekday] = draft[weekday]
            .filter(({ start, end }) => Boolean(start) && Boolean(end))
            .map(({ start, end }) => ({ start: parsePlainTime(start), end: parsePlainTime(end) }));
    }
    return hours;
}

export function cloneDraft(draft: OpeningHoursDraft): OpeningHoursDraft {
    const clone = {} as OpeningHoursDraft;
    for (const weekday of WEEKDAYS) {
        clone[weekday] = draft[weekday].map((interval) => ({ ...interval }));
    }
    return clone;
}

/** Sorts, merges overlaps and drops empties, the way `Agenda.DayTimeSliceSet` does on the server. */
export function normalizeIntervals(intervals: DraftInterval[]): DraftInterval[] {
    const sorted = intervals
        .map((interval) => ({ start: toMinutes(interval.start), end: toMinutes(interval.end) }))
        .filter((interval) => interval.start < interval.end)
        .sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];
    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && interval.start <= last.end) last.end = Math.max(last.end, interval.end);
        else merged.push({ ...interval });
    }

    return merged.map((interval) => ({ start: fromMinutes(interval.start), end: fromMinutes(interval.end) }));
}

/** The parts of `intervals` that `reference` does not cover. */
export function subtractIntervals(intervals: DraftInterval[], reference: DraftInterval[]): DraftInterval[] {
    const covered = normalizeIntervals(reference);
    const uncovered: DraftInterval[] = [];

    for (const interval of intervals) {
        const start = toMinutes(interval.start);
        const end = toMinutes(interval.end);
        if (start >= end) continue;

        let cursor = start;
        for (const slice of covered) {
            const sliceStart = toMinutes(slice.start);
            const sliceEnd = toMinutes(slice.end);
            if (sliceEnd <= cursor) continue;
            if (sliceStart >= end) break;
            if (sliceStart > cursor) {
                uncovered.push({ start: fromMinutes(cursor), end: fromMinutes(Math.min(sliceStart, end)) });
            }
            cursor = Math.max(cursor, sliceEnd);
            if (cursor >= end) break;
        }
        if (cursor < end) uncovered.push({ start: fromMinutes(cursor), end: fromMinutes(end) });
    }

    return normalizeIntervals(uncovered);
}

/**
 * The intervals a worker is bookable for on one weekday: their own grid narrowed to the
 * establishment's. A worker who follows the establishment gets its intervals unchanged.
 */
export function effectiveDayIntervals(params: {
    weekday: Plain.WeekdayName;
    businessIntervals: Plain.DayTimeSlice[];
    workerOpeningHours: PlainOpeningHours | null;
}): Plain.DayTimeSlice[] {
    const { weekday, businessIntervals, workerOpeningHours } = params;
    if (workerOpeningHours === null) return businessIntervals;

    return businessIntervals
        .flatMap((interval) =>
            workerOpeningHours[weekday].map((own) => Rich.DayTimeSlice.from(interval).intersecting(own)),
        )
        .filter((slice): slice is Rich.DayTimeSlice => slice !== null)
        .map((slice) => slice.plain);
}

/** Which endpoints of `interval` fall outside the reference hours of that day. */
export function outsideReference(
    interval: DraftInterval,
    reference: DraftInterval[],
): { start: boolean; end: boolean } {
    const start = toMinutes(interval.start);
    const end = toMinutes(interval.end);
    if (start >= end) return { start: false, end: false };
    if (subtractIntervals([interval], reference).length === 0) return { start: false, end: false };

    const startCovered = reference.some((slice) => start >= toMinutes(slice.start) && start < toMinutes(slice.end));
    const endCovered = reference.some((slice) => end > toMinutes(slice.start) && end <= toMinutes(slice.end));
    // Both endpoints land inside, but a gap in the reference sits between them.
    if (startCovered && endCovered) return { start: true, end: true };
    return { start: !startCovered, end: !endCovered };
}

function formatDayIntervals(intervals: DraftInterval[], closedLabel: string): string {
    if (intervals.length === 0) return closedLabel;
    return intervals
        .map((interval) =>
            interval.start === '00:00' && interval.end === '24:00'
                ? '24 horas'
                : `${interval.start}-${displayEnd(interval.end)}`,
        )
        .join(', ');
}

/**
 * One line per stretch of consecutive days that share the same hours, Sunday first:
 * `Segunda-Sexta: 09:00-18:00`. `closedLabel` names an empty day — `Fechado` for the
 * establishment, `Não atende` for a professional.
 */
export function summarizeOpeningHours(draft: OpeningHoursDraft, params: { closedLabel: string }): string[] {
    const groups: { label: string; days: Plain.WeekdayName[] }[] = [];

    for (const weekday of WEEKDAYS) {
        const label = formatDayIntervals(draft[weekday], params.closedLabel);
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.days.push(weekday);
        else groups.push({ label, days: [weekday] });
    }

    return groups.map((group) => {
        const first = WEEKDAY_LABELS[group.days[0]];
        if (group.days.length === 1) return `${first}: ${group.label}`;
        const last = WEEKDAY_LABELS[group.days[group.days.length - 1]];
        return `${first}-${last}: ${group.label}`;
    });
}

export function formatPlainTime(time: Plain.TimeOfDay): string {
    return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
}

function parsePlainTime(value: string): Plain.TimeOfDay {
    const [hour, minute] = value.split(':').map(Number);
    return { hour, minute };
}
